import React, { useCallback, useEffect } from "react";
import { connect } from "react-redux";
import { useTranslation } from "react-i18next";
import { loadCss } from "helpers/function";
import { verifyPermission } from "helpers/permission";
import { ERROR_401 } from "config/errorPage";
import { DeleteConfirmation } from "views/components/ConfirmationAlert";
import { ToastBottomEnd } from "views/components/Toast";
import { confirmDeleteConfig } from "config/confirmConfig";
import {
  toastDeleteErrorMessageConfig,
  toastDeleteSuccessMessageConfig,
} from "config/toastConfig";
import LoadingTable from "views/components/LoadingTable";
import EmptyTable from "views/components/EmptyTable";
import HeaderTablePage from "views/components/HeaderTablePage";
import InfirmationTable from "views/components/InfirmationTable";
import Pagination from "views/components/Pagination";
import TypeCompteRow from "components/TypeCompteRow";
import useTypeCompteData from "hooks/useTypeCompteData";

loadCss("/assets/plugins/custom/datatables/datatables.bundle.css");

const TypeCompte = ({ userPermissions }) => {
  const { t, ready } = useTranslation();

  useEffect(() => {
    if (ready) {
      document.title = t("Satis client - Paramètre Type client");
    }
  }, [ready, t]);

  const hasPermission = verifyPermission(userPermissions, "list-account-type");

  useEffect(() => {
    if (!hasPermission) {
      window.location.href = ERROR_401;
    }
  }, [hasPermission]);

  const {
    loading,
    data,
    pagination,
    handleSearch,
    handlePerPageChange,
    handlePageClick,
    handleNextPage,
    handlePreviousPage,
    handleDelete,
    totalItems,
  } = useTypeCompteData(10);

  const deleteAccountType = useCallback(
    (typeId, index) => {
      DeleteConfirmation.fire(confirmDeleteConfig()).then((result) => {
        if (result.value) {
          handleDelete(typeId, index).then((result) => {
            if (result === true) {
              ToastBottomEnd.fire(toastDeleteSuccessMessageConfig());
            } else {
              ToastBottomEnd.fire(toastDeleteErrorMessageConfig());
            }
          });
        }
      });
    },
    [handleDelete]
  );

  if (!ready || !hasPermission) return null;

  return (
    <div
      className="kt-content kt-grid__item kt-grid__item--fluid kt-grid kt-grid--hor"
      id="kt_content"
    >
      <div className="kt-subheader kt-grid__item" id="kt_subheader">
        <div className="kt-container kt-container--fluid">
          <div className="kt-subheader__main">
            <h3 className="kt-subheader__title">{t("Paramètres")}</h3>
            <span className="kt-subheader__separator kt-hidden" />
            <div className="kt-subheader__breadcrumbs">
              <span className="kt-subheader__separator kt-hidden" />
              <div className="kt-subheader__breadcrumbs">
                <button className="kt-subheader__breadcrumbs-home btn btn-link">
                  <i className="flaticon2-shelter" />
                </button>
                <span className="kt-subheader__breadcrumbs-separator" />
                <button
                  onClick={(e) => e.preventDefault()}
                  className="kt-subheader__breadcrumbs-link btn btn-link"
                >
                  {t("Type Compte")}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="kt-container kt-container--fluid kt-grid__item kt-grid__item--fluid">
        <InfirmationTable information={t("Liste des types de compte")} />

        <div className="kt-portlet">
          <HeaderTablePage
            addPermission={"store-account-type"}
            title={t("Type Compte")}
            addText={
              verifyPermission(userPermissions, "store-account-type") && t("Ajouter")
            }
            addLink={
              verifyPermission(userPermissions, "store-account-type") && "/settings/accounts/type/add"
            }
          />

          {loading ? (
            <LoadingTable />
          ) : (
            <div className="kt-portlet__body">
              <div
                id="kt_table_1_wrapper"
                className="dataTables_wrapper dt-bootstrap4"
              >
                {/* Search */}
                <div className="row">
                  <div className="col-sm-6 text-left">
                    <div id="kt_table_1_filter" className="dataTables_filter">
                      <label>
                        {t("Recherche")}:
                        <input
                          id="myInput"
                          type="text"
                          onKeyUp={(e) => handleSearch(e.target.value)}
                          className="form-control form-control-sm"
                          placeholder={t("Nom, description")}
                          aria-controls="kt_table_1"
                        />
                      </label>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <div className="row">
                  <div className="col-sm-12">
                    <table
                      className="table table-striped- table-bordered table-hover table-checkable dataTable dtr-inline"
                      id="myTable"
                      role="grid"
                      aria-describedby="kt_table_1_info"
                      style={{ width: "952px" }}
                    >
                      <thead>
                        <tr role="row">
                          <th className="sorting">{t("Nom")}</th>
                          <th className="sorting">{t("Description")}</th>
                          <th className="sorting">{t("Actions")}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.length > 0 ? (
                          data.map((type, index) => (
                            <TypeCompteRow
                              key={type.id || index}
                              type={type}
                              index={index}
                              onDelete={deleteAccountType}
                              t={t}
                              userPermissions={userPermissions}
                            />
                          ))
                        ) : (
                          <EmptyTable />
                        )}
                      </tbody>
                      <tfoot>
                        <tr></tr>
                      </tfoot>
                    </table>
                  </div>
                </div>

                {/* Pagination */}
                <div className="row">
                  <div className="col-sm-12 col-md-5">
                    <div
                      className="dataTables_info"
                      id="kt_table_1_info"
                      role="status"
                      aria-live="polite"
                    >
                      {totalItems > 0
                        ? `${t("Affichage de")} ${data.length} ${t("sur")} ${totalItems} ${t("données")}`
                        : t("Affichage de 0 page")}
                    </div>
                  </div>
                  {!pagination.isSearching && data.length > 0 && (
                    <div className="col-sm-12 col-md-7 dataTables_pager">
                      <Pagination
                        numberPerPage={pagination.perPage}
                        onChangeNumberPerPage={handlePerPageChange}
                        activeNumberPage={pagination.currentPage}
                        onClickPreviousPage={handlePreviousPage}
                        pages={pagination.pageNumbers}
                        onClickPage={handlePageClick}
                        numberPage={pagination.totalPages}
                        onClickNextPage={handleNextPage}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const mapStateToProps = (state) => ({
  userPermissions: state.user.user.permissions,
});

export default connect(mapStateToProps)(TypeCompte);
